import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Shield, MapPin, CheckCircle2, Star, Users, Heart,
  MessageSquare, UserPlus, UserMinus, ExternalLink, Briefcase,
  Award, Loader2, ChevronRight
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { authHeaders } from '../contexts/AuthContext';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from './ui/tooltip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function TrustTierBadge({ tier }) {
  const styles = {
    Exceptional: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/20',
    Strong: 'bg-blue-500/15 text-blue-600 border-blue-500/20',
    Good: 'bg-amber-500/15 text-amber-600 border-amber-500/20',
    Building: 'bg-orange-500/15 text-orange-600 border-orange-500/20',
    New: 'bg-zinc-500/15 text-zinc-500 border-zinc-500/20',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${styles[tier] || styles.New}`} data-testid="trust-tier-badge">
      <Shield className="w-2.5 h-2.5" strokeWidth={2.5} />
      {tier}
    </span>
  );
}

function DimensionBar({ label, value }) {
  const color = value >= 85 ? 'bg-emerald-500' : value >= 70 ? 'bg-amber-500' : 'bg-red-400';
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] text-muted-foreground w-[80px] flex-shrink-0">{label}</span>
      <div className="flex-1 h-1.5 rounded-full bg-secondary">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${value}%` }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className={`h-full rounded-full ${color}`}
        />
      </div>
      <span className="text-[10px] font-medium w-6 text-right tabular-nums">{value}</span>
    </div>
  );
}

export default function ProfileDrawer({ personId, onClose, onStartChat }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [followLoading, setFollowLoading] = useState(false);
  const [endorseSkill, setEndorseSkill] = useState('');
  const [endorseMsg, setEndorseMsg] = useState('');
  const [endorsing, setEndorsing] = useState(false);
  const [showEndorseForm, setShowEndorseForm] = useState(false);

  useEffect(() => {
    if (!personId) return;
    setLoading(true);
    fetch(`${API}/people/${personId}/profile`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setProfile(d); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [personId]);

  const toggleFollow = async () => {
    if (!profile) return;
    setFollowLoading(true);
    try {
      if (profile.is_following) {
        await fetch(`${API}/people/${personId}/follow`, { method: 'DELETE', headers: authHeaders() });
        setProfile(p => ({ ...p, is_following: false, follower_count: Math.max(0, (p.follower_count || 1) - 1) }));
      } else {
        await fetch(`${API}/people/${personId}/follow`, { method: 'POST', headers: authHeaders() });
        setProfile(p => ({ ...p, is_following: true, follower_count: (p.follower_count || 0) + 1 }));
      }
    } catch {}
    setFollowLoading(false);
  };

  const submitEndorsement = async (e) => {
    e.preventDefault();
    if (!endorseSkill.trim()) return;
    setEndorsing(true);
    try {
      const res = await fetch(`${API}/people/${personId}/endorse`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ skill: endorseSkill, message: endorseMsg }),
      });
      if (res.ok) {
        const endorsement = await res.json();
        setProfile(p => ({
          ...p,
          endorsement_count: (p.endorsement_count || 0) + 1,
          endorsements: [endorsement, ...(p.endorsements || [])],
        }));
        setEndorseSkill('');
        setEndorseMsg('');
        setShowEndorseForm(false);
      }
    } catch {}
    setEndorsing(false);
  };

  return (
    <AnimatePresence>
      {personId && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
            onClick={onClose}
            data-testid="profile-drawer-backdrop"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-[420px] bg-background border-l border-border overflow-y-auto"
            data-testid="profile-drawer"
          >
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : profile ? (
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-border flex-shrink-0">
                  <span className="text-xs font-medium text-muted-foreground">Lobby Profile</span>
                  <button onClick={onClose} className="p-1.5 rounded-md hover:bg-secondary transition-colors" data-testid="close-profile-drawer">
                    <X className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>

                {/* Profile Content */}
                <div className="flex-1 overflow-y-auto p-5 space-y-6">
                  {/* Identity */}
                  <div className="flex items-start gap-4">
                    <img
                      src={profile.avatar}
                      alt={profile.name}
                      className="w-16 h-16 rounded-full object-cover bg-secondary flex-shrink-0"
                      onError={e => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(profile.name)}&background=e5e5e5&color=333&size=128`; }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <h2 className="text-lg font-semibold truncate">{profile.name}</h2>
                        {profile.verified && <CheckCircle2 className="w-4 h-4 text-foreground flex-shrink-0" strokeWidth={2} />}
                      </div>
                      <p className="text-xs text-muted-foreground">{profile.role}</p>
                      <p className="text-[10px] font-mono text-muted-foreground/60 mt-0.5">{profile.ainaa_id}</p>
                    </div>
                  </div>

                  {/* Trust + Stats Row */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-600 text-sm font-bold cursor-default tabular-nums">
                            {profile.trust_score}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">Trust Score</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <TrustTierBadge tier={profile.trust_tier} />
                    <div className="flex items-center gap-3 text-[10px] text-muted-foreground ml-auto">
                      <span><strong className="text-foreground">{profile.follower_count}</strong> followers</span>
                      <span><strong className="text-foreground">{profile.following_count}</strong> following</span>
                    </div>
                  </div>

                  {/* Bio */}
                  {profile.bio && (
                    <p className="text-xs text-muted-foreground leading-relaxed">{profile.bio}</p>
                  )}

                  {/* Quick Info */}
                  <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[11px] text-muted-foreground">
                    {profile.location && (
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3" strokeWidth={1.5} />{profile.location}</span>
                    )}
                    {profile.rate && (
                      <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" strokeWidth={1.5} />{profile.rate}</span>
                    )}
                    {profile.projects_completed > 0 && (
                      <span className="flex items-center gap-1"><Star className="w-3 h-3" strokeWidth={1.5} />{profile.projects_completed} projects</span>
                    )}
                    {profile.shared_projects > 0 && (
                      <span className="flex items-center gap-1 text-foreground font-medium"><Users className="w-3 h-3" strokeWidth={1.5} />{profile.shared_projects} shared</span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      variant={profile.is_following ? 'secondary' : 'outline'}
                      size="sm"
                      className="h-8 text-[11px] gap-1.5 flex-1"
                      onClick={toggleFollow}
                      disabled={followLoading}
                      data-testid="follow-btn"
                    >
                      {followLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : profile.is_following ? <UserMinus className="w-3 h-3" /> : <UserPlus className="w-3 h-3" />}
                      {profile.is_following ? 'Following' : 'Follow'}
                    </Button>
                    {profile.connection_status === 'accepted' && onStartChat && (
                      <Button size="sm" className="h-8 text-[11px] gap-1.5 flex-1" onClick={() => onStartChat(personId)} data-testid="start-chat-btn">
                        <MessageSquare className="w-3 h-3" /> Message
                      </Button>
                    )}
                    {profile.connection_status === 'none' && (
                      <Button variant="outline" size="sm" className="h-8 text-[11px] gap-1.5 flex-1" disabled data-testid="connect-hint-btn">
                        <UserPlus className="w-3 h-3" /> Not Connected
                      </Button>
                    )}
                    {profile.connection_status === 'pending' && (
                      <Button variant="outline" size="sm" className="h-8 text-[11px] gap-1.5 flex-1" disabled>
                        Pending
                      </Button>
                    )}
                  </div>

                  {/* Trust Dimensions */}
                  <div>
                    <h3 className="text-xs font-semibold mb-3 flex items-center gap-1.5">
                      <Shield className="w-3.5 h-3.5" strokeWidth={2} /> Trust Dimensions
                    </h3>
                    <div className="space-y-2">
                      <DimensionBar label="Reliability" value={profile.reliability || 0} />
                      <DimensionBar label="Skill" value={profile.skill_score || 0} />
                      <DimensionBar label="Professionalism" value={profile.decency || 0} />
                      <DimensionBar label="Discipline" value={profile.discipline || 0} />
                    </div>
                  </div>

                  {/* Skills / Tags */}
                  {(profile.skills?.length > 0 || profile.tags?.length > 0) && (
                    <div>
                      <h3 className="text-xs font-semibold mb-2">Skills</h3>
                      <div className="flex flex-wrap gap-1.5">
                        {(profile.skills?.length > 0 ? profile.skills : profile.tags)?.map(s => (
                          <Badge key={s} variant="secondary" className="text-[10px] px-2 py-0.5 font-normal">{s}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Endorsements */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xs font-semibold flex items-center gap-1.5">
                        <Award className="w-3.5 h-3.5" strokeWidth={2} /> Endorsements
                        <span className="text-muted-foreground font-normal">({profile.endorsement_count || 0})</span>
                      </h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 text-[10px] gap-1"
                        onClick={() => setShowEndorseForm(!showEndorseForm)}
                        data-testid="toggle-endorse-form"
                      >
                        <Heart className="w-3 h-3" /> Endorse
                      </Button>
                    </div>

                    {/* Endorse Form */}
                    <AnimatePresence>
                      {showEndorseForm && (
                        <motion.form
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          onSubmit={submitEndorsement}
                          className="mb-3 p-3 rounded-lg border border-border bg-secondary/30 space-y-2 overflow-hidden"
                          data-testid="endorse-form"
                        >
                          <input
                            value={endorseSkill}
                            onChange={e => setEndorseSkill(e.target.value)}
                            placeholder="Skill (e.g., Cinematography)"
                            className="w-full h-8 px-3 rounded-md border border-border bg-background text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                            data-testid="endorse-skill-input"
                          />
                          <input
                            value={endorseMsg}
                            onChange={e => setEndorseMsg(e.target.value)}
                            placeholder="Optional message"
                            className="w-full h-8 px-3 rounded-md border border-border bg-background text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                            data-testid="endorse-message-input"
                          />
                          <Button type="submit" size="sm" className="h-7 text-[10px] w-full" disabled={!endorseSkill.trim() || endorsing} data-testid="submit-endorsement-btn">
                            {endorsing ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Submit Endorsement'}
                          </Button>
                        </motion.form>
                      )}
                    </AnimatePresence>

                    {/* Skill Endorsement Groups */}
                    {profile.skill_endorsements && Object.keys(profile.skill_endorsements).length > 0 ? (
                      <div className="space-y-2">
                        {Object.entries(profile.skill_endorsements).map(([skill, endorsers]) => (
                          <div key={skill} className="flex items-center gap-2 p-2 rounded-lg bg-secondary/40">
                            <Badge variant="outline" className="text-[10px] px-2 py-0.5 flex-shrink-0">{skill}</Badge>
                            <div className="flex -space-x-1.5 flex-1">
                              {endorsers.slice(0, 4).map((e, i) => (
                                <TooltipProvider key={i} delayDuration={0}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="w-5 h-5 rounded-full bg-muted border border-background flex items-center justify-center text-[8px] font-semibold cursor-default">
                                        {e.from_name?.[0]?.toUpperCase() || '?'}
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="text-[10px]">
                                      <p className="font-medium">{e.from_name}</p>
                                      {e.message && <p className="text-muted-foreground mt-0.5 italic">"{e.message}"</p>}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ))}
                              {endorsers.length > 4 && (
                                <div className="w-5 h-5 rounded-full bg-muted border border-background flex items-center justify-center text-[7px] text-muted-foreground">
                                  +{endorsers.length - 4}
                                </div>
                              )}
                            </div>
                            <span className="text-[9px] text-muted-foreground flex-shrink-0">{endorsers.length}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[10px] text-muted-foreground/60 italic">No endorsements yet</p>
                    )}
                  </div>

                  {/* Portfolio */}
                  {profile.portfolio?.length > 0 && (
                    <div>
                      <h3 className="text-xs font-semibold mb-2">Portfolio</h3>
                      <div className="space-y-2">
                        {profile.portfolio.map(p => (
                          <a
                            key={p.proof_id}
                            href={p.url || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 p-2 rounded-lg border border-border/40 hover:border-border transition-all text-xs"
                          >
                            <ExternalLink className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                            <span className="truncate">{p.title || p.url}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {profile.portfolio_url && (
                    <a
                      href={profile.portfolio_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <ExternalLink className="w-3 h-3" /> View full portfolio
                      <ChevronRight className="w-3 h-3 ml-auto" />
                    </a>
                  )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-border flex-shrink-0">
                  <p className="text-[9px] text-muted-foreground/40 text-center">
                    Profile powered by Lobby · Trust verified by AINAA
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
                Profile not found
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
